// src/components/Dispatchdetails/DispatchQuickView.jsx
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Divider,
  TextField,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import NearMeIcon from "@mui/icons-material/NearMe";
import EditIcon from "@mui/icons-material/Edit";
import { useMemo, useState, useEffect } from "react";
import { db } from "../../firebase";
import { doc, updateDoc, arrayUnion, getDoc } from "firebase/firestore";
import ReusableFormExceptions from "../ReusableFormExceptions/ReusableFormExceptions";
import { translateStatesSendsFalabbelaSelector } from "../../utils/objects/translateStates";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

function formatTs(ts) {
  try {
    if (!ts) return null;
    if (typeof ts?.toDate === "function") return ts.toDate().toLocaleString();
    if (typeof ts?.seconds === "number")
      return new Date(ts.seconds * 1000).toLocaleString();
    if (typeof ts?._seconds === "number")
      return new Date(ts._seconds * 1000).toLocaleString();
    if (typeof ts === "string") return ts;
    return null;
  } catch {
    return null;
  }
}
function hasAny(obj) {
  if (!obj) return false;
  return Object.values(obj).some(
    (v) =>
      v !== null &&
      v !== undefined &&
      v !== "" &&
      !(Array.isArray(v) && v.length === 0)
  );
}
function Row({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.9 }}>
        {value}
      </Typography>
    </Box>
  );
}
function Section({ title, children }) {
  if (
    !children ||
    (Array.isArray(children) && children.filter(Boolean).length === 0)
  )
    return null;
  return (
    <Box
      sx={{
        p: 1.25,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        {title}
      </Typography>
      <Stack spacing={0.5}>{children}</Stack>
    </Box>
  );
}

export default function DispatchQuickView({
  open,
  onClose,
  order, // objeto de orden
  onCrearTicket, // (opcional)
  onCorregirDatos, // (opcional)
  onEnviarExcepcion, // (opcional)
  onLiberarConductor, // (opcional)
  onAbrirDetalle, // (opcional)
  compyOrder, // (opcional) -> necesario para excepciones (.nonDelivery)
  firebaseUser = {},
}) {
  // ❌ NO pongas "if (!order) return null;" antes de los hooks

  // ----- state hooks (siempre se ejecutan) -----
  const [newComent, setNewComent] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [openException, setOpenException] = useState(false);
  const [riderDestails, setRiderDetails] = useState({});

  // Usamos un wrapper seguro para evitar null checks en useMemo
  const o = order || {};

  // ----- memos (siempre se ejecutan) -----
  const datos = useMemo(
    () => ({
      id: o.id,
      status: o.status,
      deliveryType: o.deliveryType,
      tag: o.tag,
      createdAt: formatTs(o.createdAt || o.date),
      updatedAt: formatTs(o.updatedAt),
      placeName: o.placeName,
      fromAddress: o.fromAddress,
      fromLatLng: o?.fromAddressLocation
        ? `${o.fromAddressLocation.lat ?? o.fromAddressLocation._lat ?? ""}, ${
            o.fromAddressLocation.lng ??
            o.fromAddressLocation._long ??
            o.fromAddressLocation._lng ??
            ""
          }`
        : null,
      amount: o.amount,
      deliveryAmount: o.deliveryAmount,
      serviceAmount: o.serviceAmount,
      driverAmount: o.driverAmount,
      totalAmount: o.totalAmount,
    }),
    [o]
  );

  const cliente = useMemo(
    () => ({
      userName: o.userName,
      userPhone: o.userPhone,
      userEmail: o.userEmail,
      userDniReceptor: o.userDniReceptor,
      userNameReceptor: o.userNameReceptor,
      observationReceptor: o.observationReceptor,
    }),
    [o]
  );

  const operacionalesDriver = useMemo(
    () => ({
      driverName: o.driverName,
      driverPhone: o.driverPhone,
      driverLicensePlate: o.driverLicensePlate,
      driverAmount: o.driverAmount,
      driverLatLng: o?.driverLocation
        ? `${o.driverLocation.lat ?? o.driverLocation._lat ?? ""}, ${
            o.driverLocation.lng ??
            o.driverLocation._long ??
            o.driverLocation._lng ??
            ""
          }`
        : null,
      isPickedUp: o.isPickedUp ? "Sí" : "No",
      lastMile: o.lastMile ? "Sí" : "No",
      express: o.express ? "Sí" : "No",
    }),
    [o]
  );

  const entrega = useMemo(
    () => ({
      toAddress: o.toAddress,
      toLatLng: o?.toAddressLocation
        ? `${o.toAddressLocation.lat ?? o.toAddressLocation._lat ?? ""}, ${
            o.toAddressLocation.lng ??
            o.toAddressLocation._long ??
            o.toAddressLocation._lng ??
            ""
          }`
        : null,
      picsTaken: o.picsTaken ? "Sí" : "No",
      signTaken: o.signTaken ? "Sí" : "No",
      picsCount: Array.isArray(o.pics) ? o.pics.length : 0,
      signUrl: o.sign,
      pics: o.pics ? o.pics : [],
    }),
    [o]
  );

  useEffect(() => {
    let mounted = true;
    const fetchRiderDetails = async (driverId) => {
      try {
        if (!driverId) return;
        const docRef = doc(
          db,
          process.env.REACT_APP_COL_USERS_DRIVERS,
          driverId
        );
        const riderRef = await getDoc(docRef);
        if (!mounted) return;
        setRiderDetails(riderRef.data() || {});
      } catch (error) {
        console.error("Error al obtener detalles del rider:", error);
      }
    };

    if (order?.driverID) {
      fetchRiderDetails(order.driverID);
    } else {
      setRiderDetails({});
    }
  }, [order]);

  const showDatos = hasAny(datos);
  const showCliente = hasAny(cliente);
  const showOperacionales = hasAny(operacionalesDriver);
  const showEntrega = hasAny(entrega);

  const canOpenException = useMemo(
    () => Boolean(translateStatesSendsFalabbelaSelector),
    [translateStatesSendsFalabbelaSelector]
  );

  const missingForException = useMemo(() => {
    const missing = [];
    if (!translateStatesSendsFalabbelaSelector)
      missing.push("compyOrder.nonDelivery");
    return missing;
  }, [translateStatesSendsFalabbelaSelector]);

  // ----- funciones -----
  const saveComentAdmin = async (e) => {
    e?.preventDefault?.();
    const text = newComent.trim();
    if (!text || !o?.id) return;
    const docID = "F" + o.id;

    try {
      setIsSending(true);
      const orderRef = doc(db, process.env.REACT_APP_COL_USERS_ORDERS, docID);
      await updateDoc(orderRef, { observationAdmin: arrayUnion(text) });
      setNewComent("");
      onCrearTicket?.({ id: docID, comment: text });
    } catch (err) {
      console.error("saveComentAdmin:", err);
    } finally {
      setIsSending(false);
    }
  };

  const handleExceptionSave = (payload) => {
    if (!payload || payload.phase !== "success") return;

    console.log("Payload excepción:", payload);
    // El propio ReusableFormExceptions llamará a handleClose()
    // así que no hace falta setOpenException(false) aquí.

    onEnviarExcepcion?.(o, payload); // callback externo opcional
  };

  // ✅ Ahora sí podemos cortar si no hay order, pero DESPUÉS de los hooks
  if (!order) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ color: "#030303" }}>
        <SupportAgentIcon
          sx={{
            width: "40px !important",
            height: "40px !important",
            color: "#1e2158 !important",
            backgroundColor: "#d1eaff !important",
            borderRadius: "50%",
          }}
        />
        Vista rápida — {o.id}
      </DialogTitle>

      <DialogContent dividers>
        {/* Cabecera compacta */}
        <Stack direction="row" spacing={2} sx={{ mb: 1, alignItems: "center" }}>
          <Typography variant="body2">
            <b>Estado:</b> {o.status ?? "—"}
          </Typography>
          {o?.driverName && (
            <Typography variant="body2">
              <b>Driver:</b> {o.driverName}
            </Typography>
          )}
          {o?.driverLicensePlate && (
            <Typography variant="body2">
              <b>Ruta:</b> {o.driverLicensePlate}
            </Typography>
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {/* Grid 2x2 */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr", md: "1fr 1fr" },
            gap: 1.5,
          }}
        >
          {/* Columna izquierda */}
          <Box sx={{ display: "grid", gap: 1.5 }}>
            {showDatos && (
              <Section title="Datos">
                <Row label="ID" value={datos.id} />
                <Row label="Estado" value={datos.status} />
                <Row label="Tipo entrega" value={datos.deliveryType} />
                <Row label="Tag" value={datos.tag} />
                <Row label="Creado" value={datos.createdAt} />
                <Row label="Actualizado" value={datos.updatedAt} />
                <Row label="Origen" value={datos.fromAddress} />
                <Row label="Origen (lat,lng)" value={datos.fromLatLng} />
                <Row label="Local" value={datos.placeName} />
                {/* // Futuro:
                // <Row label="Monto servicio" value={datos.serviceAmount} />
                // <Row label="Monto delivery" value={datos.deliveryAmount} />
                // <Row label="Monto driver" value={datos.driverAmount} />
                // <Row label="Total" value={datos.totalAmount} /> */}
              </Section>
            )}

            {showCliente && (
              <Section title="Cliente">
                <Row label="Nombre" value={cliente.userName} />
                <Row label="Teléfono" value={cliente.userPhone} />
                <Row label="Email" value={cliente.userEmail} />
                <Row label="Receptor" value={cliente.userNameReceptor} />
                <Row label="DNI Receptor" value={cliente.userDniReceptor} />
                <Row label="Observación" value={cliente.observationReceptor} />
              </Section>
            )}
          </Box>

          {/* Columna derecha */}
          <Box sx={{ display: "grid", gap: 1.5 }}>
            {showOperacionales && (
              <Section title="Operacionales / Driver">
                <Row label="Driver" value={operacionalesDriver.driverName} />
                <Row
                  label="Teléfono Driver"
                  value={operacionalesDriver.driverPhone}
                />
                <Row
                  label="Ruta"
                  value={operacionalesDriver.driverLicensePlate}
                />
                <Row
                  label="Driver (lat,lng)"
                  value={operacionalesDriver.driverLatLng}
                />
                <Row label="Picked" value={operacionalesDriver.isPickedUp} />
              </Section>
            )}

            {showEntrega && (
              <Section title="Entrega">
                <Row label="Dirección" value={entrega.toAddress} />
                <Row label="Destino (lat,lng)" value={entrega.toLatLng} />
                <Row label="Fotos tomadas" value={entrega.picsTaken} />
                <Row label="Firma tomada" value={entrega.signTaken} />
                <Row label="Cantidad fotos" value={entrega.picsCount} />
                <Row label="URL firma" value={entrega.signUrl} />
              </Section>
            )}
          </Box>
        </Box>

        {/* Comentar (embebido) */}
        <Box
          component="form"
          onSubmit={saveComentAdmin}
          sx={{
            mt: 2,
            p: 1,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            Agregar comentario a la Orden
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              placeholder="Agregar comentario"
              variant="outlined"
              type="text"
              fullWidth
              onChange={(e) => setNewComent(e.target.value)}
              value={newComent}
              InputProps={{ startAdornment: <EditIcon sx={{ mr: 1 }} /> }}
            />
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={isSending || !newComent.trim()}
              startIcon={isSending ? <CircularProgress size={14} /> : null}
              sx={{
                backgroundColor: "#591E8F",
                ":hover": {
                  backgroundColor: "#25b2f4 !important",
                  color: "#591e8f",
                },
                whiteSpace: "nowrap",
              }}
            >
              Guardar Nota
            </Button>
          </Stack>
        </Box>

        {/* Ir a detalle completo */}
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            startIcon={<NearMeIcon />}
            onClick={() => onAbrirDetalle?.(o)}
          >
            Ir a detalle completo
          </Button>
        </Box>
      </DialogContent>

      <DialogActions>
        {/* // Mantener referenciado para futuras funciones:
        // <Button onClick={() => onCrearTicket?.(o)} variant="outlined">Crear ticket</Button>
        // <Button onClick={() => onCorregirDatos?.(o)} variant="outlined">Corregir datos</Button>
        */}
        <Tooltip
          title={
            canOpenException ? "" : `Falta: ${missingForException.join(", ")}`
          }
        >
          <span>
            <Button
              onClick={() => {
                if (canOpenException) setOpenException(true);
              }}
              variant="contained"
              sx={{ backgroundColor: "red", color: "#FFF" }}
              disabled={!canOpenException}
            >
              Motivo de no entrega
            </Button>
          </span>
        </Tooltip>

        {/* // Futuro:
        // <Button onClick={() => onLiberarConductor?.(o)} variant="contained" color="warning">
        //   Liberar conductor
        // </Button>
        */}
        <Button onClick={onClose} sx={{ color: "red" }}>
          Cerrar
        </Button>
      </DialogActions>

      {/* Modal de Excepción */}
      {canOpenException && (
        <ReusableFormExceptions
          compyOrder={compyOrder}
          open={openException}
          handleClose={() => setOpenException(false)}
          onSave={handleExceptionSave}
          orderID={o.id}
          riderDestails={
            riderDestails?.driverLocation ? riderDestails.driverLocation : null
          }
          direct={true}
          directSelectReason={translateStatesSendsFalabbelaSelector}
          user={firebaseUser}
        />
      )}
    </Dialog>
  );
}
